import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import verycard from "../../assets/images/rewardimg.png";
import { AnimatePresence, motion } from "framer-motion";
import mtnLogo from "../../assets/images/mtn.png";
import gloLogo from "../../assets/images/glo.png";
import airtelLogo from "../../assets/images/airtel1.png";
import ninemobileLogo from "../../assets/images/9mobile.png";
import { apiClient, getCashbackBalance, getMyWallet } from "../../api/apiServices";
import { useNavigate } from "react-router-dom";
import BannerSlider from "../dashboard/components/BannerSlider";

const AirtimeDashboard = () => {
  const images = [verycard];

  const navigate = useNavigate()

  const [current, setCurrent] = useState(0);
  const [recipient, setRecipient] = useState("");
  const [network, setNetwork] = useState("");
  const [networks, setNetworks] = useState([]);
  const [detectedNetwork, setDetectedNetwork] = useState("");
  const [availableBalance, setAvailableBalance] = useState(0);
  const [recentNumbers, setRecentNumbers] = useState(
    JSON.parse(localStorage.getItem("recentNumbers") || "[]")
  );

  const airtimeAmounts = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  const [customAmount, setCustomAmount] = useState("");
  const [showPayModal, setShowPayModal] = useState(false);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [cashbackBalance, setCashbackBalance] = useState(null);
  const [hasCashback, setHasCashback] = useState(false);
  const [cashbackError, setCashbackError] = useState(null);

  

  const networkLogos = {
    MTN: mtnLogo,
    GLO: gloLogo,
    AIRTEL: airtelLogo,
    "9MOBILE": ninemobileLogo,
  };

  const networkPrefixes = {
    MTN: [
      "0803","0806","0810","0813","0814","0816","0703","0706","0903","0906","0913","0916","07025","07026","0704",
    ],
    GLO: ["0805","0807","0811","0815","0705","0905","0915"],
    AIRTEL: ["0802","0808","0812","0701","0708","0901","0902","0904","0907","0912"],
    "9MOBILE": ["0809","0817","0818","0908","0909"],
  };

  // Fetch wallet balance
useEffect(() => {
  const fetchBalance = async () => {
    try {
      const res = await getMyWallet();
      const balance = res?.balance ?? 0;
      setAvailableBalance(balance);
    } catch (err) {
      console.error("Failed to fetch wallet balance:", err);
      setAvailableBalance(0);
    }
  };
  fetchBalance();
}, []);

  // Fetch networks from API
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const res = await apiClient.get("/vtu/airtime/networks");
        if (res.data?.data?.networks) {
          setNetworks(res.data.data.networks);
          if (res.data.data.networks.length > 0) {
            setNetwork(res.data.data.networks[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch networks:", err);
      }
    };
    fetchNetworks();
  }, []);

  // Auto-detect network from phone
useEffect(() => {
  const phone = recipient.trim().replace(/\D/g, "");
  if (phone.length < 4) {
    setDetectedNetwork("");
    return;
  }

  let foundNetworkName = "";
  for (const [net, prefixes] of Object.entries(networkPrefixes)) {
    if (prefixes.some((p) => phone.startsWith(p))) {
      foundNetworkName = net; // e.g., "MTN"
      setDetectedNetwork(net);
      break;
    }
  }

  if (foundNetworkName) {
    // Sync the dropdown (network state) with the detected one
    // We search the fetched networks list for a name that matches our detection
    const match = networks.find(
      (n) => n.name.toUpperCase() === foundNetworkName.toUpperCase()
    );
    if (match) {
      setNetwork(match.id);
    }
  } else {
    setDetectedNetwork("");
  }
}, [recipient, networks]);

  const loadCashbackData = async () => {
        try {
          setCashbackError(null);
          const  cashbackRes = await getCashbackBalance()
          setCashbackBalance(cashbackRes?.cashbackBalance);
        } catch (err) {
          console.error("Failed to load referral data:", err);
          setCashbackError("Could not load reward data. Please try again.");
        }
      };
  
    // ====================== EFFECTS ======================
    useEffect(() => {
      loadCashbackData()
    }, []);

  // Carousel auto-slide
  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images]);

  const isAmountValid = () => {
    const amt = Number(customAmount);
    return amt >= 50 && amt <= 50000;
  };

  const handleProceedToPay = () => {
    if (!recipient.trim()) {
      setErrorMessage("Enter phone number");
      return;
    }
    if (!isAmountValid()) {
      setErrorMessage("Enter a valid amount (₦50 - ₦50,000)");
      return;
    }
    setShowPayModal(true);
  };

  const handleBuyAirtime = async () => {
    if (!pin || pin.length !== 4) {
      setErrorMessage("Enter a 4-digit PIN");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const res = await apiClient.post("/vtu/airtime", {
        mobile_number: recipient.trim(),
        network: (detectedNetwork || network).toLowerCase(),
        amount: Number(customAmount),
        pin,
        hasCashback,
      });

      if (res.data?.message?.toLowerCase().includes("submitted")) {
        setSuccessMessage("Airtime request submitted successfully! 🎉");

        // Save to recent numbers (persist in localStorage)
        if (!recentNumbers.includes(recipient)) {
          const updatedRecents = [recipient, ...recentNumbers.slice(0, 9)];
          setRecentNumbers(updatedRecents);
          localStorage.setItem("recentNumbers", JSON.stringify(updatedRecents));
        }

        // Reset form
        setShowPayModal(false);
        setCustomAmount("");
        setRecipient("");
        setPin("");
      } else {
        setErrorMessage(res.data?.message || "Airtime purchase failed");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Airtime purchase failed");
    } finally {
      setLoading(false);
    }
  };

  // Filter recent numbers for suggestions
  const filteredRecents = recentNumbers.filter((num) =>
    num.includes(recipient.trim())
  );

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <div onClick={handleBack} className="text-[17px] font-bold text-[#3866a3]">← Airtime</div>
          <h3 className="text-sm font-medium text-gray-600">History</h3>
        </div>
        
        <div className="px-4 pt-5 max-w-md mx-auto">
          {/* Carousel */}
          <BannerSlider />
        </div>
        

        <div className="px-4 pt-5 space-y-8 max-w-md mx-auto">

          {/* Phone Input */}
          <div className="space-y-2 relative">
            <label className="text-gray-600 text-sm font-medium">
              Recipient Phone Number
            </label>
            <div className="flex items-center border rounded-full px-3 py-2.5 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-400 transition">
              {(() => {
    const selectedNetworkObj = networks.find(n => n.id === network);
    const logoKey = selectedNetworkObj ? selectedNetworkObj.name.toUpperCase() : "";
    
    return networkLogos[logoKey] ? (
      <img
        src={networkLogos[logoKey]}
        alt="logo"
        className="w-7 h-7 mr-2.5 rounded-full object-cover border border-gray-200"
      />
    ) : (
      <span className="w-7 h-7 mr-2.5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600">
        ?
      </span>
    );
  })()}
              <input
                type="tel"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base font-medium"
                placeholder="0803 XXX XXXX"
                maxLength={11}
              />
            </div>

            {/* Show detected network */}
            {detectedNetwork && (
              <p className="text-xs text-blue-600 font-medium mt-1 pl-3">
                {detectedNetwork}
              </p>
            )}

            {/* Recent numbers dropdown */}
            {recipient.length >= 3 && filteredRecents.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-64 overflow-y-auto">
                {filteredRecents.map((num, idx) => (
                  <div
                    key={idx}
                    onClick={() => setRecipient(num)}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">
                      {num.slice(0, 2)}
                    </div>
                    <span className="text-gray-800">{num}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Network Selector */}
          {/* {networks.length > 1 && (
            <div className="space-y-2">
              <label className="text-gray-600 text-sm font-medium">Network</label>
              <select
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
                className="w-full border rounded-full px-4 py-3 bg-white outline-none"
              >
                {networks.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* Amount */}
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
  <div className="flex justify-between items-center px-1">
    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-tight">Select Amount</h2>
    {customAmount && <span className="text-blue-600 font-bold text-sm">₦{Number(customAmount).toLocaleString()}</span>}
  </div>

  <div className="grid grid-cols-3 gap-2">
    {airtimeAmounts.map((amt) => (
      <button
        key={amt}
        type="button"
        onClick={() => setCustomAmount(amt.toString())}
        className={`rounded-lg py-3 text-center transition-all border ${
          customAmount === amt.toString()
            ? "bg-[#0f3d91] text-white border-blue-600 shadow-sm"
            : "bg-gray-50 border-gray-100 text-gray-600 hover:bg-blue-50"
        }`}
      >
        <div className="text-sm font-bold">₦{amt}</div>
      </button>
    ))}
  </div>

  <div className="relative">
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">₦</span>
    <input
      type="number"
      value={customAmount}
      onChange={(e) => setCustomAmount(e.target.value)}
      placeholder="Enter other amount"
      className="w-full bg-gray-50 border border-gray-100 rounded-xl pl-8 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:bg-white transition-all"
    />
  </div>
</div>

          {/* --- NEW CASHBACK SECTION START --- */}
{/* <div className="bg-white rounded-2xl shadow-md p-5">
  <div className="flex flex-row justify-between items-center">
    <span className="text-gray-600 text-lg">Cashback</span>
    <span className={`font-semibold text-lg ${hasCashback ? "text-green-600" : "text-gray-400"}`}>
      {hasCashback ? `Using ₦${cashbackBalance.toLocaleString()}` : "Not used"}
    </span>
  </div>
  <div className="h-px bg-gray-100 my-4" />

  <div className="flex flex-row justify-between items-center">
    <div>
      <p className="text-gray-800 font-medium">Use Cashback Balance</p>
      <p className="text-gray-500 text-xs">
        Available: ₦{cashbackBalance?.toLocaleString()}
      </p>
    </div>

    <button
      type="button"
      onClick={() => setHasCashback((prev) => !prev)}
      className={`w-14 h-8 rounded-full px-1 flex items-center transition-colors duration-200 focus:outline-none ${
        hasCashback ? "bg-green-500" : "bg-gray-300"
      }`}
    >
      <div
        className={`w-6 h-6 rounded-full bg-white transition-transform duration-200 transform ${
          hasCashback ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </button>
  </div>
</div> */}

          {/* Messages */}
          {successMessage && (
            <div className="text-green-600 text-center font-medium py-2">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="text-red-600 text-center font-medium py-2">
              {errorMessage}
            </div>
          )}

          {/* Proceed Button */}
          <button
            onClick={handleProceedToPay}
            disabled={!customAmount || loading || !isAmountValid()}
            className={`w-full py-4 rounded-full font-semibold text-white transition-all ${
              customAmount && isAmountValid() && !loading
                ? "bg-[#0f3d91] hover:bg-[#0d347a] shadow-md"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? "Processing..." : "Proceed to Pay"}
          </button>
        </div>
      </div>

      {/* PIN Modal */}
      <AnimatePresence>
        {showPayModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-sm"
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-bold">Enter Transaction PIN</h3>
                <button onClick={() => setShowPayModal(false)}>
                  <X size={24} />
                </button>
              </div>

              <input
                type="password"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="****"
                className="w-full text-center text-3xl tracking-widest border-b-2 border-gray-300 py-4 outline-none focus:border-blue-600 mb-6"
              />

              <button
                onClick={handleBuyAirtime}
                disabled={loading || pin.length !== 4}
                className={`w-full py-3.5 rounded-full font-semibold text-white ${
                  pin.length === 4 && !loading
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {loading ? "Processing..." : "Confirm Payment"}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
  Available balance: ₦{Number(availableBalance).toLocaleString()}
</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MobileBottomNav />
    </>
  );
};

export default AirtimeDashboard;